import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormControl, Select, MenuItem, InputLabel } from '@material-ui/core';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { NotNestedDropDownElement, GetPassengerTypes, SearchModes } from '../../../../constants';
import RangeSetting from '../../../RangeSetting';

const StateroomFilling = ({ staterooms, roomIndex, onChangeFilter, disabled }) => {
  const { track } = useAnalytics();
  const room = staterooms[roomIndex];
  const { travelAgency = {} } = useSelector((store) => store.user);
  const enableBookingForMinors =
    travelAgency && travelAgency.settings && travelAgency.settings.enableBookingForMinors;
  const hasMinimalAdultSeniorCount = room.adults + room.seniors === 1;
  const { selectedSearchMode } = useSelector((store) => store.tripSearch);

  const passengerTypes = useMemo(
    () => GetPassengerTypes(travelAgency.enabledTransportModes, selectedSearchMode),
    [travelAgency.enabledTransportModes, selectedSearchMode],
  );

  const childrenAges = useMemo(() => {
    if (passengerTypes.children) {
      const { fromAge, toAge } = passengerTypes.children;
      return _.map(_.range(fromAge, toAge + 1), (r) => ({
        label: `${r} year${r > 1 ? 's' : ''}`,
        value: r,
      }));
    }
    return [];
  }, [passengerTypes.children]);

  const handleDecrementPassengers = useCallback(
    (property) => {
      const totalPassengers = ['adults', 'seniors', 'children'].reduce((sum, key) => {
        const value = room[key];
        if (Array.isArray(value)) {
          return sum + value.length;
        }
        return sum + value;
      }, 0);
      if (
        totalPassengers === 1 &&
        (property === 'adults' || property === 'seniors' || property === 'children')
      ) {
        return;
      }

      if (
        property !== 'children' &&
        property !== 'lapInfants' &&
        property !== 'seatInfants' &&
        hasMinimalAdultSeniorCount &&
        !(enableBookingForMinors && selectedSearchMode === SearchModes.FLIGHT)
      ) {
        return;
      }
      let newValue = room[property];
      if (property === 'children') {
        newValue = room[property].length > 0 ? newValue.slice(0, newValue.length - 1) : newValue;
      } else {
        newValue = newValue > 0 ? newValue - 1 : 0;
      }
      const updatedStaterooms = [...staterooms];
      updatedStaterooms[roomIndex] = {
        ...updatedStaterooms[roomIndex],
        [property]: newValue,
      };
      if ((property === 'adults' || property === 'seniors') && room.adults + room.seniors === 1) {
        // Infants can't search/book a trip without adults
        updatedStaterooms[roomIndex] = {
          ...updatedStaterooms[roomIndex],
          [property]: newValue,
          lapInfants: 0,
          seatInfants: 0,
        };
      }
      onChangeFilter({ staterooms: updatedStaterooms });
      track('filterBar.decrementPassengers', {
        [property]: newValue.join ? newValue.join() : newValue,
      });
    },
    [
      enableBookingForMinors,
      hasMinimalAdultSeniorCount,
      onChangeFilter,
      room,
      roomIndex,
      selectedSearchMode,
      staterooms,
      track,
    ],
  );

  const handleIncrementPassengers = useCallback(
    (property) => {
      let newValue = room[property];
      if (
        !(room.adults || room.seniors) &&
        (property === 'lapInfants' || property === 'seatInfants')
      ) {
        return;
      }
      if (property === 'children') {
        newValue = [...newValue, childrenAges[0].value];
      } else {
        newValue += 1;
      }

      const updatedStaterooms = [...staterooms];
      updatedStaterooms[roomIndex] = {
        ...updatedStaterooms[roomIndex],
        [property]: newValue,
      };

      onChangeFilter({ staterooms: updatedStaterooms });
      track('filterBar.incrementPassengers', {
        [property]: newValue.join ? newValue.join() : newValue,
      });
    },
    [onChangeFilter, room, roomIndex, staterooms, track, childrenAges],
  );

  const handleAgeChange = useCallback(
    (value, index) => {
      const newValue = [...room.children];
      newValue[index] = value;
      const updatedStaterooms = [...staterooms];
      updatedStaterooms[roomIndex] = {
        ...updatedStaterooms[roomIndex],
        children: newValue,
      };
      onChangeFilter({ staterooms: updatedStaterooms });
      track('filterBar.handleAgeChange', { children: newValue.join() });
    },
    [onChangeFilter, room.children, roomIndex, staterooms, track],
  );

  return (
    <>
      {Object.values(passengerTypes).map(({ type, label, helperText }) => (
        <RangeSetting
          key={type}
          type={type}
          label={label}
          helperText={helperText}
          onIncrement={disabled ? null : handleIncrementPassengers}
          onDecrement={disabled ? null : handleDecrementPassengers}
          value={type === 'children' ? room[type].length : room[type]}
        />
      ))}
      {room.children.length
        ? room.children.map((item, index) => (
            <div
              key={`child-age-select-${index.toString()}`}
              className="items-center col-span-3 flex justify-between p-2"
            >
              <div className="font-bold">Child {index + 1}</div>
              <FormControl variant="outlined" className="w-1/2">
                <InputLabel>Age</InputLabel>
                <Select
                  value={item}
                  onChange={(event) => handleAgeChange(event.target.value, index)}
                  label="Age"
                >
                  {childrenAges.map((age) => (
                    <MenuItem
                      key={age.label}
                      value={Number.parseInt(age.value, 10)}
                      className={NotNestedDropDownElement}
                    >
                      {age.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          ))
        : ''}
    </>
  );
};

StateroomFilling.propTypes = {
  staterooms: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  roomIndex: PropTypes.number.isRequired,
  onChangeFilter: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

StateroomFilling.defaultProps = {
  disabled: false,
};

export default StateroomFilling;
